import type { ReactNode } from 'react';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  confirmLabel: string;
  onConfirm: () => void;
  isConfirming?: boolean;
  /** Disables the confirm button independent of isConfirming (e.g. a required field is unfilled). */
  confirmDisabled?: boolean;
  /** Arbitrary body content rendered between the description and the footer (e.g. a reason field). */
  children?: ReactNode;
  /** The dismiss button's label. Defaults to "Cancel". */
  cancelLabel?: string;
  /** The confirm button's look. Defaults to `danger`, for destructive actions. */
  confirmVariant?: 'danger' | 'navy' | 'green';
  /** The dismiss button's look. Defaults to `outline`. */
  cancelVariant?: 'outline' | 'green';
  /**
   * `inline` (default): the pair sits right-aligned on one row.
   * `stacked`: full-width buttons, confirm on top - the hero-style decision
   * dialogs (cancel subscription, auto-renewal) in the 2026-09-22 design.
   */
  layout?: 'inline' | 'stacked';
  /** Centred icon above the title; pair with `align="center"`. */
  headerIcon?: ReactNode;
  align?: 'left' | 'center';
}

/**
 * The footer classes per layout, resolved once.
 *
 * `stacked` uses `flex-col-reverse` so the confirm button renders on top while
 * the safe action still comes first in DOM and tab order.
 */
const FOOTER_LAYOUT: Record<NonNullable<ConfirmDialogProps['layout']>, string> = {
  inline: 'flex justify-end gap-3 [&>*]:whitespace-nowrap',
  stacked: 'flex flex-col-reverse gap-3 [&>*]:w-full',
};

/**
 * Shared confirm/alert flow composing the single `Dialog` primitive
 * (mnr-standards: feature dialogs never re-implement overlay/focus-trap).
 * Used for destructive actions (e.g. Delete Profile).
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onConfirm,
  isConfirming = false,
  confirmDisabled = false,
  children,
  cancelLabel = 'Cancel',
  confirmVariant = 'danger',
  cancelVariant = 'outline',
  layout = 'inline',
  headerIcon,
  align,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      headerIcon={headerIcon}
      align={align}
      footer={
        // One wrapper so Dialog's `[&>*]:w-full` applies to it rather than to each
        // button, letting the pair sit inline and save a row of modal height.
        //
        // Right-aligned and sized to content rather than stretched: these buttons
        // carry the member-screen box model (px-8 py-4), so forcing them to half
        // the modal width made a two-word label wrap onto a second line and left
        // two oversized blocks. `whitespace-nowrap` keeps the label on one line.
        //
        // Cancel leads in DOM/tab order: on a destructive confirm the safe action
        // should be the one a keyboard user reaches first.
        <div className={FOOTER_LAYOUT[layout]}>
          <Button variant={cancelVariant} onClick={() => onOpenChange(false)} disabled={isConfirming}>
            {cancelLabel}
          </Button>
          <Button variant={confirmVariant} onClick={onConfirm} disabled={isConfirming || confirmDisabled}>
            {confirmLabel}
          </Button>
        </div>
      }
    >
      {children}
    </Dialog>
  );
}
